import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  IonContent,
  IonIcon,
  IonPage,
  IonSpinner,
  useIonAlert,
  useIonRouter,
} from "@ionic/react";
import { useActor, useSelector } from "@xstate/react";
import {
  BookingContextPersistedSnapshotSchema,
  BookingMachineApiRequirements,
  bookingMachineWithApi,
  PracticalInfoChange,
  PracticalInfoForm,
  PracticalInfoFormSchema,
} from "./CreateBookingMachineV2";
import { useRouteMatch } from "react-router-dom";
import { CartActorProvider, useCartActor } from "./Cart/CartProvider";
import {
  Calendar,
  NumberPicker,
  TimePicker,
  Spinner,
  BookTableBox,
  LazyCoverImage,
  FloatingBackButton,
  GradientButton,
  useHiddenTabBar,
  useAuth,
  TextField,
  formatPrice,
} from "@greeter/matter";
import { useEffect } from "react";
import {
  InputSection,
  InputSectionContent,
  InputSectionError,
  InputSectionSummary,
  InputSectionSummaryContent,
  InputSectionSummaryTitle,
} from "./InputSection";
import { isFirstOfJanuary, isNewYearsEve, TimeOfDay } from "@greeter/date";
import { tableIds, tableIdString, Venue } from "@greeter/core";
import {
  addHours,
  addSeconds,
  differenceInDays,
  getDaysInMonth,
  isSameDay,
  minutesToMilliseconds,
  subDays,
} from "date-fns";
import { BundlePickerSelector } from "./BundlePicker/BundlePickerSelector";
import { CartBottomBar } from "./Cart/CartBottomBar";
import { CartContent } from "./Cart/CartContent";
import { classNames, doNothing, isFacebookBrowser } from "@greeter/util";
import { LoginPage } from "../LoginPage";
import { UpdateUserPageApiHandler } from "../UpdateUserPage/UpdateUserPageApiHandler";
import {
  Checkout,
  DeclineError,
  translateStripeErrorToDanish,
} from "@greeter-guest/components/Payments";
import Routes, { BookRoute, Tab } from "@greeter-guest/utility/Routes";
import { useDetectTab } from "@greeter-guest/hooks";
import { usePersistedCache } from "@greeter/cache";
import { queries, useSearchParams } from "@greeter/url";
import { Snapshot } from "xstate";
import {
  calendarOutline,
  chatboxOutline,
  createOutline,
  cubeOutline,
  personOutline,
  timeOutline,
} from "ionicons/icons";
import { indexBy, lookupBy } from "@greeter/map";
import { summarizeTables } from "./utils";
import { every } from "lodash";
import { CartContextSchema } from "./Cart/CartMachine";
import { PersistedSnapshotSchema } from "@greeter/xstate-util";
import { ApiError } from "@greeter/api";
import {
  InputGroupProvider,
  useInputGroup,
} from "./InputSection/InputGroupProvider";
import * as SetUtil from "@greeter/set";
import { useAnonymousAnalyticsPlugin } from "./AnonymousAnalytics";
import { getEnv } from "@greeter-guest/utility/ConfigHooks";
import { animate, stagger } from "motion";
import { assert } from "@greeter/assert";
import { ToastContainer, ToastContentProps, toast } from "react-toastify";
import { AnimatePresence, motion } from "motion/react";
import z from "zod";
import { dropInOutFromBottom, dropInOutFromTop } from "@greeter/motion";
import { Log } from "@greeter/log";
import { BottomBar } from "@greeter/matter-v2";
import { Helmet } from "@dr.pogodin/react-helmet";

import css from "./CreateBookingPageV2.module.scss";

function printErrors(errors?: String[]) {
  if (!errors) return "";

  return errors.join(", ");
}

function SplitButtons({
  closeToast,
  ...props
}: ToastContentProps<{
  title?: string;
  message: string;
  buttons: { onClick: () => void; text: string }[];
}>) {
  return (
    <div className={css.SplitButtons}>
      <div className={css.SplitButtonsContent}>
        {props.data.title && <h3 className={css.Title}>{props.data.title}</h3>}
        <p className={css.Message}>{props.data.message}</p>
      </div>
      <div className={css.ButtonContainer}>
        {props.data.buttons.map((b) => {
          return (
            <button
              onClick={() => {
                b.onClick();
                closeToast();
              }}
              className={css.Button}
            >
              {b.text}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function openSupportChatHandler(bookingId?: string, errorCode?: string) {
  return () => {
    const msg = `Hej! Jeg har problemer med booking: ${bookingId ?? "ukendt"}${
      errorCode ? ` med fejlkode: ${errorCode}` : ""
    }\n\n`;
    window.open(
      `https://m.me/Greeternightlife?text=${encodeURIComponent(msg)}`,
      "_blank"
    );
  };
}

function ChatSupportBubble(props: { offsetBottom?: number; attributes?: any }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLParagraphElement>(null);
  const yRef = useRef(0);
  const [rightOffset, setRightOffset] = useState(0);

  useEffect(() => {
    function findPosition() {
      if (!containerRef.current) return;
      const windowWidth = window.innerWidth;
      const maxWidthArea = 600;

      if (windowWidth > maxWidthArea) {
        setRightOffset((windowWidth - maxWidthArea) / 2);
      }
    }
    findPosition();
    window.addEventListener("resize", findPosition);

    return () => window.removeEventListener("resize", findPosition);
  });

  useEffect(() => {
    if (!containerRef.current) return;
    const _offsetBottom = (props.offsetBottom ?? 0) * -1;
    const y = [
      yRef.current === _offsetBottom ? 0 : yRef.current,
      _offsetBottom,
    ];
    animate(containerRef.current, {
      y,
    });
    yRef.current = _offsetBottom;
  }, [props.offsetBottom]);

  useEffect(() => {
    if (!textRef.current) return;
    animate(textRef.current, { y: [2, 0], opacity: [0, 1] });
    const v = textRef.current.querySelector("#wave");
    assert(v, "requires the wave component to run");
    const tw = 30;
    animate(
      v,
      {
        rotate: [0, 15, -15, 15, -15, 15, -15, 15, -15, 0],
        scale: [2, 2, 2, 2, 2, 2, 2, 2, 2, 1],
        x: [tw, tw, tw, tw, tw, tw, tw, tw, tw, 0],
      },
      { duration: 1.5 }
    );
    const letters = Array.from(textRef.current.querySelectorAll(".letter"));
    animate(
      letters,
      { opacity: [0, 1] },
      { delay: stagger(0.05, { startDelay: 1.5 }), duration: 0.5 }
    );
    animate(
      letters,
      { y: [0, -3, 0] },
      { delay: stagger(0.1, { startDelay: 5 }) }
    );
  }, []);

  return (
    <motion.div
      key="chat"
      ref={containerRef}
      className={css.ChatBubbleContainer}
      style={{
        right: `calc(clamp(${rightOffset}px, 0px, ${Number.MAX_SAFE_INTEGER}px) + var(--vertical-padding))`,
      }}
      onClick={() => {
        // We do this, so it's easy to simply plugin chats like Jivo or Tawk
        window.dispatchEvent(
          new CustomEvent("greeter:open-support", { detail: props.attributes })
        );
        // window.open("https://m.me/Greeternightlife", "_blank")
      }}
    >
      <div className={css.ChatBubbleContainerText} ref={textRef}>
        <span style={{ marginRight: 5 }} id="wave">
          👋
        </span>
        <span className="letter" style={{ opacity: 0 }}>
          S
        </span>
        <span className="letter" style={{ opacity: 0 }}>
          u
        </span>
        <span className="letter" style={{ opacity: 0 }}>
          p
        </span>
        <span className="letter" style={{ opacity: 0 }}>
          p
        </span>
        <span className="letter" style={{ opacity: 0 }}>
          o
        </span>
        <span className="letter" style={{ opacity: 0 }}>
          r
        </span>
        <span className="letter" style={{ opacity: 0 }}>
          t
        </span>
      </div>
    </motion.div>
  );
}

function SummaryTitle(props: React.PropsWithChildren) {
  return <div className={css.SummaryTitle}>{props.children}</div>;
}

export function SummaryError(props: React.PropsWithChildren) {
  return <div className={css.SummaryError}>{props.children}</div>;
}

function SummaryLabel(props: React.PropsWithChildren) {
  return <div className={css.SummaryLabel}>{props.children}</div>;
}

function SummaryContent(props: React.PropsWithChildren) {
  return <div className={css.SummaryContent} {...props} />;
}

function SummaryIcon(props: { error?: boolean } & React.PropsWithChildren) {
  return (
    <div {...classNames(css.SummaryIcon, props.error && css.Error)}>
      {props.children}
    </div>
  );
}

function SummaryItem(props: React.PropsWithChildren) {
  return <div className={css.SummaryItem} {...props} />;
}

function SummaryItemContainer(props: React.PropsWithChildren) {
  return <div className={css.SummaryItemContainer} {...props} />;
}

function EditButton(props: React.ComponentProps<"button">) {
  return (
    <button
      style={{ border: "none", backgroundColor: "transparent" }}
      className={css.EditButton}
      {...props}
    />
  );
}

const dateOnlyFormatter = Intl.DateTimeFormat("da-DK", {
  month: "long",
  day: "numeric",
});
const dateTimeFormatter = Intl.DateTimeFormat("da-DK", {
  month: "long",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});
const summaryDateFormatter = dateTimeFormatter;

type PracticalInfoSummaryProps = {
  practicalInfo: Partial<PracticalInfoForm>;
  onEdit?: (
    target:
      | "group-size"
      | "arrival-date"
      | "arrival-time"
      | "tables"
      | "products"
      | "comment"
  ) => void;
};
function PracticalInfoSummary(props: PracticalInfoSummaryProps) {
  const parsed = PracticalInfoFormSchema.safeParse(props.practicalInfo);
  const errors = parsed.error ? z.treeifyError(parsed.error) : { errors: [] };

  return (
    <div className={css.PracticalInfoSummary}>
      <SummaryItemContainer>
        <SummaryItem>
          <SummaryIcon error={!!errors?.properties?.groupSize?.errors[0]}>
            <IonIcon style={{ color: "white" }} icon={personOutline} />
          </SummaryIcon>
          <SummaryContent>
            <SummaryTitle>
              {props.practicalInfo.groupSize
                ? props.practicalInfo.groupSize
                : "Gruppestørrelse ikke valgt endnu"}
            </SummaryTitle>
            <SummaryLabel>
              {props.practicalInfo.groupSize === 1 ? "Person" : "Personer"}
            </SummaryLabel>
          </SummaryContent>
          <EditButton onClick={() => props.onEdit?.call(null, "group-size")}>
            <IonIcon
              style={{ color: "white", width: 20, height: 20 }}
              icon={createOutline}
            />
          </EditButton>
        </SummaryItem>
        {errors?.properties?.groupSize?.errors[0] && (
          <SummaryError>{errors.properties?.groupSize?.errors[0]}</SummaryError>
        )}
      </SummaryItemContainer>
      <SummaryItemContainer>
        <SummaryItem>
          <SummaryIcon error={!!errors?.properties?.arrivalDate?.errors[0]}>
            <IonIcon style={{ color: "white" }} icon={calendarOutline} />
          </SummaryIcon>
          <SummaryContent>
            <SummaryTitle>
              {props.practicalInfo.arrivalDate
                ? dateOnlyFormatter.format(props.practicalInfo.arrivalDate)
                : "Dato er ikke valgt endnu"}
            </SummaryTitle>
            <SummaryLabel>Dato</SummaryLabel>
          </SummaryContent>
          <EditButton onClick={() => props.onEdit?.call(null, "arrival-date")}>
            <IonIcon
              style={{ color: "white", width: 20, height: 20 }}
              icon={createOutline}
            />
          </EditButton>
        </SummaryItem>
        {errors?.properties?.arrivalDate?.errors[0] && (
          <SummaryError>
            {errors.properties?.arrivalDate?.errors[0]}
          </SummaryError>
        )}
      </SummaryItemContainer>
      <SummaryItemContainer>
        <SummaryItem>
          <SummaryIcon error={!!errors?.properties?.arrivalTime?.errors[0]}>
            <IonIcon style={{ color: "white" }} icon={timeOutline} />
          </SummaryIcon>
          <SummaryContent>
            <SummaryTitle>
              {props.practicalInfo.arrivalTime &&
              props.practicalInfo.arrivalDate
                ? `Kl. ${TimeOfDay.fromDate(
                    props.practicalInfo.arrivalTime
                  ).toString()}${
                    isSameDay(
                      props.practicalInfo.arrivalDate,
                      props.practicalInfo.arrivalTime
                    )
                      ? ""
                      : ` (over midnat ${dateFormatter.format(
                          props.practicalInfo.arrivalTime
                        )})`
                  }`
                : "Tidspunkt er ikke valgt endnu"}
            </SummaryTitle>
            <SummaryLabel>Tidspunkt</SummaryLabel>
          </SummaryContent>
          <EditButton onClick={() => props.onEdit?.call(null, "arrival-time")}>
            <IonIcon
              style={{ color: "white", width: 20, height: 20 }}
              icon={createOutline}
            />
          </EditButton>
        </SummaryItem>
        {errors?.properties?.arrivalTime?.errors[0] && (
          <SummaryError>
            {errors.properties?.arrivalTime?.errors[0]}
          </SummaryError>
        )}
      </SummaryItemContainer>
      <SummaryItemContainer>
        <SummaryItem>
          <SummaryIcon
            error={
              !!errors?.properties?.tables?.errors[0] ||
              !!errors?.properties?.total?.errors[0]
            }
          >
            <IonIcon style={{ color: "white" }} icon={cubeOutline} />
          </SummaryIcon>
          <SummaryContent>
            <SummaryTitle>
              {props.practicalInfo.tables
                ? summarizeTables(props.practicalInfo.tables)
                : "Ingen borde valgt endnu"}
            </SummaryTitle>
            <SummaryLabel>Pladser</SummaryLabel>
          </SummaryContent>
          <EditButton onClick={() => props.onEdit?.call(null, "tables")}>
            <IonIcon
              style={{ color: "white", width: 20, height: 20 }}
              icon={createOutline}
            />
          </EditButton>
        </SummaryItem>
        {errors?.properties?.tables?.errors[0] && (
          <SummaryError>{errors.properties?.tables?.errors[0]}</SummaryError>
        )}
        {errors?.properties?.total?.errors[0] && (
          <SummaryError>{errors.properties?.total?.errors[0]}</SummaryError>
        )}
      </SummaryItemContainer>
      <SummaryItemContainer>
        <SummaryItem>
          <SummaryIcon error={!!errors?.properties?.comment?.errors[0]}>
            <IonIcon style={{ color: "white" }} icon={chatboxOutline} />
          </SummaryIcon>
          <SummaryContent>
            <SummaryTitle>
              {props.practicalInfo.comment !== undefined &&
              props.practicalInfo.comment !== ""
                ? props.practicalInfo.comment
                : "Ingen kommentar"}
            </SummaryTitle>
            <SummaryLabel>Kommentar</SummaryLabel>
          </SummaryContent>
          <EditButton onClick={() => props.onEdit?.call(null, "comment")}>
            <IonIcon
              style={{ color: "white", width: 20, height: 20 }}
              icon={createOutline}
            />
          </EditButton>
        </SummaryItem>
        {errors?.properties?.comment?.errors[0] && (
          <SummaryError>{errors.properties?.comment?.errors[0]}</SummaryError>
        )}
      </SummaryItemContainer>
    </div>
  );
}

const dateFormatter = Intl.DateTimeFormat("da-DK", {
  year: "numeric",
  day: "numeric",
  month: "short",
});

export type CreateBookingPageApiRequirements = BookingMachineApiRequirements;

export type CreateBookingPageV2Props = {
  venueId: string;
  api: BookingMachineApiRequirements;
};

type Intent = "check-payment";

export function CreateBookingPageV2(props: CreateBookingPageV2Props) {
  useHiddenTabBar();

  const auth = useAuth();

  const searchParams = useSearchParams();
  const intent: Intent | null = searchParams.get("intent") as Intent;
  const bookingId: string | null = searchParams.get("bookingId");

  const cartCache = useCartCache({ venueId: props.venueId });
  const bookingCache = useBookingCache({ venueId: props.venueId });

  const env = getEnv();

  const [snap, send, m] = useActor(bookingMachineWithApi(props.api), {
    id: "booking-machine",
    input: { venueId: props.venueId },
    snapshot: intent === "check-payment" ? bookingCache.get() : undefined,
  });

  useAnonymousAnalyticsPlugin(m);

  useEffect(() => {
    if (intent === "check-payment") {
      send({ type: "restart" });
      send({ type: "continueFetching" });
    }
  }, [intent]);

  useEffect(() => {
    if (intent === "check-payment" && bookingId && auth.isLoggedIn) {
      send({ type: "setBookingId", data: bookingId });
    }
  }, [send, intent, auth.isLoggedIn]);

  const practicalInfo = useSelector(
    m,
    (s) => s.context.practicalInfoChanges.value
  );
  const practicalInfoErrors = useSelector(
    m,
    (s) => s.context.practicalInfoValidationErrors
  );
  const specialPricePeriods = useSelector(
    m,
    (s) => s.context.specialPricePeriods
  );
  const activePeriods = useSelector(m, (s) => s.context.activePeriods);

  useEffect(() => {
    const tag = `[CreateBookingPageV2][bookingMachine]`;
    const sub2 = m.on("*", (emitted) => Log.debug(`${tag}[event]`, emitted));
    return () => {
      // sub1.unsubscribe();
      sub2.unsubscribe();
    };
  }, [m]);

  const changePracticalInfo = useCallback(
    function changePracticalInfo(change: PracticalInfoChange) {
      send({ type: "changePracticalInfo", data: change });
    },
    [send]
  );

  const venue = useSelector(m, (s) => s.context.venue);
  const specialOpeningHours = useSelector(
    m,
    (s) => s.context.specialOpeningHours
  );
  const greeterEvents = useSelector(m, (s) => s.context.greeterEvents);
  const bookingSettings = useSelector(m, (s) => s.context.bookingSettings);
  const floorPlan = useSelector(m, (s) => s.context.floorPlan);
  const bookedTables = useSelector(m, (s) => s.context.bookedTables);
  const lockedTables = useSelector(m, (s) => s.context.lockedTables);
  const bundles = useSelector(m, (s) => s.context.bundles);
  const ticketEvents = useSelector(m, (s) => s.context.ticketEvents);
  const ticketEventBundles = useSelector(
    m,
    (s) => s.context.ticketEventBundles
  );
  const booking = useSelector(m, (s) => s.context.booking);
  const minPrice = useSelector(
    m,
    (s) => s.context.practicalInfoChanges.value.minPrice
  );
  const stripePaymentToken = useSelector(
    m,
    (s) => s.context.stripePaymentToken
  );
  const venueId = useSelector(m, (s) => s.context.venueId);

  const openingHours = useMemo(
    () =>
      venue && specialOpeningHours && practicalInfo.arrivalDate
        ? Venue.findOpeningHoursAsDatePeriodForDate(
            venue,
            practicalInfo.arrivalDate,
            specialOpeningHours
          )
        : undefined,
    [venue, specialOpeningHours, practicalInfo.arrivalDate]
  );

  const cart = useCartActor();
  const cartCount = useSelector(cart, (s) => s.context.count);
  const cartTotal = useSelector(cart, (s) => s.context.total);

  useEffect(() => {
    changePracticalInfo({ type: "setTotal", data: cartTotal });
  }, [cartTotal]);

  useEffect(() => {
    return cart.on("*", () => {
      send({ type: "setCart", data: cart.getSnapshot().context.state.value });
      changePracticalInfo({
        type: "setTotal",
        data: cartTotal,
      });
    }).unsubscribe;
  }, [cartTotal, send, changePracticalInfo]);

  useEffect(
    () =>
      m.on("failedPayment", () => {
        m.send({ type: "openCartModal" });
      }).unsubscribe,
    [m]
  );

  useEffect(
    () =>
      m.on("createdPayment", () => {
        bookingCache.save(m.getPersistedSnapshot());
        Log.debug("Saved to booking cache:", bookingCache.get());
        cartCache.save(cart.getPersistedSnapshot());
        Log.debug("Saved to cart cache:", cartCache.get());
      }).unsubscribe,
    [m, bookingCache, cartCache]
  );

  const pageRef = useRef<HTMLElement>(null);
  const tab = useDetectTab();
  const [alert] = useIonAlert();
  // const [toast] = useIonToast();

  useEffect(() => {
    async function signin() {
      if (auth.isLoggedIn) {
        return;
      }

      try {
        Log.debug("signing in anonymously");
        await auth.auth?.signInAsAnon();
        Log.debug("succesfully signed in");
      } catch (error) {
        console.error("failed to signin anonymously", error);
        throw error;
      }
    }

    signin();
  }, [auth.auth, auth.isLoggedIn]);

  useEffect(
    () =>
      m.on("createdBooking", () => {
        send({ type: "openCartModal" });
        send({ type: "createPayment" });
      }).unsubscribe,
    [m]
  );
  useEffect(
    () =>
      m.on("createdPayment", () => {
        send({ type: "openPaymentModal" });
      }).unsubscribe,
    [m]
  );

  const router = useIonRouter();

  useEffect(
    () =>
      m.on("completedBooking", ({ data: { bookingId: _bookingId } }) => {
        if (tab === Tab.Venues) {
          router.push(
            Routes.venuesTab.bookingSummary.route({ bookingId: _bookingId! }),
            "forward",
            "push",
            { unmount: true }
          );
          return;
        } else if (tab === Tab.Events) {
          router.push(
            Routes.greeterEventsTab.bookingSummary.route({
              bookingId: _bookingId!,
            }),
            "forward",
            "push",
            { unmount: true }
          );
          return;
        }

        router.push(
          Routes.bookingSummary.route({ bookingId: _bookingId! }),
          "forward",
          "push",
          { unmount: true }
        );
      }).unsubscribe,
    [m, router, bookingId]
  );

  useEffect(() => {
    const subs = [
      m.on("failedToValidateBookingWasPaidFor", () => {
        alert({
          mode: "ios",
          header: "Betalingen er ikke gået igennem",
          message:
            "Nogle gange kan Stripe være lidt sløv til at snakke med vores backend. Bare lige vent 5 minutter og se om ikke du modtager en email!",
          buttons: [{ text: "Ok" }],
        });
      }),
      m.on("failedToCreateBooking", (error) => {
        const tag = `[failedToCreateBooking]`;
        console.warn(
          `received error when trying to create booking`,
          error,
          error.error instanceof ApiError
        );
        if (error.error instanceof ApiError) {
          switch (error.error.details.data.errorCode) {
            case "tryingToBookBookedTables": // fallthrough
            case "tryingToBookLockedTables":
              alert({
                mode: "ios",
                header: "Kunne ikke booke bordene",
                message:
                  "Et eller flere af de borde du har valgt er allerede bookede",
                buttons: [
                  {
                    text: "Fravælg utilgængelige borde",
                    handler() {
                      inputGroup.open("tables");
                    },
                  },
                  { text: "Ignorer" },
                ],
              });
              return;

            case "invalidOrderState": // fallthrough
            case "invalidBookingState":
              alert({
                mode: "ios",
                header: "Kan ikke opdatere bookingen",
                message:
                  "Din booking er allerede blevet betalt eller blevet accepteret. Du kan derfor ikke opdatere din booking. Tjek din email efter en booking mail",
                buttons: [
                  {
                    text: "Opret ny booking",
                    handler() {
                      console.log(
                        tag,
                        "refreshing cart line IDs so we don't try to overwrite existing orderlines"
                      );
                      cart.send({ type: "refreshIds" });
                      console.log(
                        tag,
                        "setting the booking to undefined so we don't try to update the booking"
                      );
                      send({ type: "setBooking", data: undefined });
                      setTimeout(() => send({ type: "createBooking" }), 500);
                    },
                  },
                  {
                    text: "Skriv til support",
                    handler: openSupportChatHandler(
                      booking?.id,
                      error.error.details.data.errorCode
                    ),
                  },
                  { text: "Ignorer" },
                ],
              });
              return;

            case "invalidBundleLineQuantity": // fallthrough
            case "invalidBundleLineVariantQuantity": // fallthrough
            case "missingBundleLineVariant": // fallthrough
            case "missingBundleLine":
              alert({
                mode: "ios",
                header: "Kunne ikke oprette bookingen",
                message: `En af linjerne i en af de bordpakker du har bygget er ikke længere tilgængelig eller har et mismatch i kvantitet. Det kan ske hvis ${
                  venue?.name ?? "venueet"
                } er har opdateret bordpakken`,
                buttons: [
                  {
                    text: "Genvælg bordpakke",
                    handler() {
                      cart.send({ type: "reset" });
                      send({ type: "closeModal" });
                      send({ type: "openBundlePickerModal" });
                    },
                  },
                  {
                    text: "Skriv til support",
                    handler: openSupportChatHandler(
                      booking?.id,
                      error.error.details.data.errorCode
                    ),
                  },
                  { text: "Ignorer" },
                ],
              });
              return;

            default:
              alert({
                mode: "ios",
                header: "Kunne ikke oprette booking",
                message: "Tjek og se om du har udfyldt alle felterne",
                buttons: [
                  { text: "Ok" },
                  {
                    text: "Prøv igen",
                    handler() {
                      send({ type: "createBooking" });
                    },
                  },
                  {
                    text: "Skriv til support",
                    handler: openSupportChatHandler(
                      booking?.id,
                      error.error.details.data.errorCode
                    ),
                  },
                ],
              });
              return;
          }
        }
        alert({
          mode: "ios",
          header: "Kunne ikke oprette booking",
          message: "Tjek og se om du har udfyldt alle felterne",
          buttons: [{ text: "Ok" }],
        });
      }),
      m.on("failedToCreatePayment", (error) => {
        alert({
          header: "Kunne ikke oprette betaling",
          mode: "ios",
          buttons: [
            { text: "Ok" },
            {
              text: "Skriv til support",
              handler: openSupportChatHandler(
                booking?.id,
                (error.error as ApiError)?.details?.data?.errorCode
              ),
            },
          ],
        });
      }),
    ];

    return () => subs.forEach((s) => s.unsubscribe());
  }, [m]);

  const unavailableDatesFn = useCallback(
    (year: number, month: number) => {
      const start = new Date(year, month);
      const end = addHours(new Date(), 1);
      const openingHours = Venue.createDatePeriodsFromOpeningHours({
        openingHours: venue!.bookableOpeningHours,
        specialOpeningHours: specialOpeningHours,
        range: differenceInDays(end, start),
        now: start,
      });
      return openingHours.map((oh) => oh.from);
    },
    [venue, specialOpeningHours]
  );

  const datesFn = useCallback(
    (year: number, month: number) => {
      const start = new Date(year, month);
      const openingHours = Venue.createDatePeriodsFromOpeningHours({
        openingHours: venue!.bookableOpeningHours,
        specialOpeningHours: specialOpeningHours,
        range: getDaysInMonth(start),
        now: start,
      });
      return openingHours.map((oh) => oh.from);
    },
    [venue?.bookableOpeningHours, specialOpeningHours]
  );

  const onCalendarChange = useCallback(
    function (d: Date): void {
      function updateArrivalDate() {
        changePracticalInfo({
          type: "setArrivalDate",
          data: d,
        });
        cart.send({ type: "reset" });
      }

      if (cartCount > 0 || practicalInfo.tables.length > 0) {
        alert({
          mode: "ios",
          header: "Ændring af dato vil rydde kurven og valg af borde",
          message:
            "Stedet kan have forskellige bordpakker, tilbud eller lukning af borde/områder på forskellige tidspunkter" +
            ", derfor vil din kurv blive ryddet og du skal vælge borde og drikkevare igen." +
            '\nHvis du er indforstået med det, og gerne vil forsætte, tryk "Ja, fortsæt"',
          buttons: [
            { text: "Fortryd" },
            {
              text: "Ja, fortsæt",
              handler: updateArrivalDate,
            },
          ],
        });
        return;
      }

      updateArrivalDate();
    },
    [changePracticalInfo, cartCount]
  );

  const isInputValid = useSelector(
    m,
    (s) =>
      !s.context.practicalInfoValidationErrors ||
      Array.from(
        Object.entries(
          s.context?.practicalInfoValidationErrors?.properties ?? {}
        ) ?? []
      ).length === 0
  );

  const conflictingTables = useSelector(m, (s) => {
    let bookedOrLockedTables = new Set(
      s.context.bookedTables.concat(s.context.lockedTables).map(tableIdString)
    );
    let picked = new Set(
      s.context.practicalInfoChanges.value.tables.map(tableIdString)
    );
    if (s.context.booking) {
      const bookingTableIds = new Set(
        tableIds(s.context.booking.tables).map(tableIdString)
      );
      bookedOrLockedTables = SetUtil.difference(
        bookingTableIds,
        bookedOrLockedTables
      );
    }
    const conflicting = SetUtil.intersection(bookedOrLockedTables, picked);
    return Array.from(conflicting);
  });

  const inputGroup = useInputGroup();

  const [settled, setSettled] = useState(false);
  const settle = useCallback(
    async function settle() {
      send({ type: "pauseFetching" });
      await new Promise<void>((resolve, reject) => {
        function saveCaches() {
          bookingCache.save(m.getPersistedSnapshot());
          cartCache.save(cart.getPersistedSnapshot());
          Log.debug("Saved to cache, booking:", bookingCache.get());
          Log.debug("Saved to cache, cart:", cartCache.get());
        }
        let subscription = m.subscribe((s) => {
          // In case we never reach the "idle" state across all
          // lets save and continue;
          const id = setTimeout(() => {
            Log.debug(
              "background states never settled, rejecting",
              backgroundStates.join(", ")
            );
            reject(
              new Error(
                "background states never settled, rejecting: " +
                  backgroundStates.join(", ")
              )
            );
          }, 10_000);
          const backgroundStates = Array.from(
            Object.values(s.value.background)
          );
          Log.debug(
            "checking backgroundstates\n",
            Array.from(Object.entries(s.value.background))
              .map((a) => a.join(": "))
              .join("\n")
          );
          if (every(backgroundStates, (s) => s === "idle")) {
            setSettled(true);
            Log.debug("backgroundStates are idle!", backgroundStates);
            resolve(undefined);
            saveCaches();
            clearTimeout(id);
            subscription.unsubscribe();
          }
        });
      });
    },
    [send, snap, bookingCache, cartCache]
  );
  useEffect(() => m.on("cartModalOpened", settle).unsubscribe, [m, settle]);
  useEffect(
    () =>
      m.on("cartModalClosed", () =>
        send({ type: "setBackgroundFetchMode", data: "fetching" })
      ).unsubscribe,
    [m]
  );
  useEffect(
    () => m.on("paymentMethodPickerModalOpened", settle).unsubscribe,
    [m, settle]
  );
  useEffect(
    () =>
      m.on("paymentMethodPickerModalClosed", () =>
        send({ type: "setBackgroundFetchMode", data: "fetching" })
      ).unsubscribe,
    [m]
  );

  useEffect(() => {
    if (isFacebookBrowser()) {
      document.body.style.setProperty("--facebook-padding", "0px");
    }

    return () => void document.body.style.removeProperty("--facebook-padding");
  }, []);

  const bookTableBox: React.ReactNode = !!floorPlan &&
    practicalInfo.arrivalTime && (
      <BookTableBox
        env={env}
        now={practicalInfo.arrivalTime}
        display="capacity"
        total={cartTotal}
        selectableBookedTables={tableIds(booking?.tables ?? new Map())}
        bookedTables={bookedTables.concat(lockedTables) ?? []}
        floorPlan={floorPlan ?? { areas: [] }}
        groupSize={practicalInfo.groupSize ?? 0}
        onTableClick={(area, table) => {
          changePracticalInfo({
            type: "selectTable",
            data: {
              area: area,
              tableNumber: table.number,
              minPrice: table.minPrice ?? 0,
            },
          });
        }}
        selectedTables={practicalInfo.tables}
        onMinimumPriceChange={(newMin) => {
          changePracticalInfo({
            type: "setMinPrice",
            data: newMin,
          });
        }}
        featureFlags={{
          overlayOnOpeningHoursViolation: true,
        }}
        specialOpeningHours={specialOpeningHours ?? []}
      />
    );

  const bookingJsonLd = venue
    ? {
        "@context": "https://schema.org",
        "@type": "FoodEstablishment",
        name: venue.name,
        description: venue.description,
        image: venue.coverUrl,
        address: {
          "@type": "PostalAddress",
          streetAddress: venue.address.street,
          addressLocality: venue.address.city,
          postalCode: venue.address.postalCode,
          addressCountry: venue.address.country,
        },
        potentialAction: {
          "@type": "ReserveAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: `https://app.greeter.dk/create-booking/${venue.id}`,
            actionPlatform: [
              "https://schema.org/DesktopWebPlatform",
              "https://schema.org/MobileWebPlatform",
              "https://schema.org/IOSPlatform",
              "https://schema.org/AndroidPlatform",
            ],
          },
          result: {
            "@type": "Reservation",
            name: `Book bord hos ${venue.name}`,
          },
        },
      }
    : null;

  return (
    <IonPage ref={pageRef}>
      {venue && (
        <Helmet>
          <title>Book bord - {venue.name}</title>
          <meta name="og:title" content={`Book bord - ${venue.name}`} />
          <meta name="og:description" content={venue.description} />
          <meta name="og:image" content={venue.coverUrl} />
          <script type="application/ld+json">
            {JSON.stringify(bookingJsonLd)}
          </script>
        </Helmet>
      )}
      <AnimatePresence mode="popLayout">
        {snap.matches({ modal: "closed" }) &&
          !snap.matches({ ui: "paying" }) && (
            <IonContent
              style={{ backgroundColor: "var(--gm-color-default-bg)" }}
            >
              <motion.div
                key="main-flow"
                layout="position"
                className={css.CreateBookingPage}
                {...dropInOutFromBottom}
              >
                <div
                  className={css.Cover}
                  style={{ aspectRatio: "16 / 7", position: "relative" }}
                >
                  <LazyCoverImage src={venue?.coverAsset?.uri} />
                  {venue && <h1 className={css.BookTableTitle}>Book bord</h1>}
                </div>
                {snap.matches({ ui: "practicalInfo" }) && (
                  <div className={css.Form}>
                    <InputSection
                      id="group-size"
                      invalid={
                        !!(
                          (practicalInfoErrors?.properties?.groupSize?.errors
                            ?.length ?? 0) > 0
                        )
                      }
                      // open={inputSections.inputOpenMap?.get("group-size")}
                    >
                      <InputSectionSummary>
                        <InputSectionSummaryTitle>
                          Gæster
                        </InputSectionSummaryTitle>
                        <NumberPicker
                          min={0}
                          value={practicalInfo.groupSize ?? 0}
                          onChange={(n) =>
                            changePracticalInfo({
                              type: "setGroupSize",
                              data: n,
                            })
                          }
                        />
                      </InputSectionSummary>
                    </InputSection>

                    <InputSection
                      id="arrival-date"
                      disabled={!!practicalInfoErrors?.properties?.groupSize}
                      invalid={!!practicalInfoErrors?.properties?.arrivalDate}
                    >
                      <InputSectionSummary>
                        <InputSectionSummaryTitle>
                          Dato
                        </InputSectionSummaryTitle>
                        <InputSectionSummaryContent>
                          {practicalInfo.arrivalDate &&
                          isNewYearsEve(practicalInfo.arrivalDate) &&
                          isFirstOfJanuary(practicalInfo.arrivalDate)
                            ? "(Nytår) " +
                              dateFormatter.format(
                                subDays(practicalInfo.arrivalDate, 1)
                              )
                            : dateFormatter.format(practicalInfo.arrivalDate)}
                          {practicalInfo.arrivalDate &&
                          practicalInfo.arrivalTime &&
                          !isSameDay(
                            practicalInfo.arrivalTime,
                            practicalInfo.arrivalDate
                          )
                            ? ` - ${dateFormatter.format(
                                practicalInfo.arrivalTime
                              )}`
                            : ""}
                        </InputSectionSummaryContent>
                      </InputSectionSummary>
                      <InputSectionContent>
                        <div style={{ padding: "1rem" }}>
                          <Calendar
                            unavailableDatesFn={unavailableDatesFn}
                            datesFn={datesFn}
                            events={greeterEvents}
                            selected={practicalInfo.arrivalDate}
                            onChange={onCalendarChange}
                          />
                        </div>
                      </InputSectionContent>
                    </InputSection>

                    <InputSection
                      id="arrival-time"
                      disabled={
                        !!practicalInfoErrors?.properties?.arrivalDate ||
                        !openingHours
                      }
                      invalid={!!practicalInfoErrors?.properties?.arrivalTime}
                    >
                      <InputSectionSummary>
                        <InputSectionSummaryTitle>
                          Klokkeslæt
                        </InputSectionSummaryTitle>
                        <InputSectionSummaryContent>
                          {practicalInfo.arrivalTime && (
                            <span>
                              {TimeOfDay.fromDate(
                                practicalInfo.arrivalTime
                              ).toString()}
                            </span>
                          )}
                        </InputSectionSummaryContent>
                      </InputSectionSummary>
                      <InputSectionContent>
                        <div style={{ padding: "1rem" }}>
                          {openingHours && (
                            <TimePicker
                              interval={15}
                              range={openingHours}
                              isEnabled={(d) => {
                                const now = new Date();
                                if (
                                  practicalInfo.arrivalDate &&
                                  isSameDay(now, practicalInfo.arrivalDate)
                                ) {
                                  const buffered = addSeconds(
                                    now,
                                    bookingSettings?.buffer.getTotalSeconds() ??
                                      0
                                  );
                                  return d > buffered;
                                }

                                return true;
                              }}
                              selected={practicalInfo.arrivalTime}
                              anchorDate={practicalInfo.arrivalDate}
                              onChange={(d) => {
                                function updateArrivalTime() {
                                  changePracticalInfo({
                                    type: "setArrivalTime",
                                    data: d,
                                  });
                                  send({ type: "fetchBookingsForArrivalDate" });
                                  cart.send({ type: "reset" });
                                }

                                if (
                                  cartCount > 0 ||
                                  practicalInfo.tables.length > 0
                                ) {
                                  alert({
                                    mode: "ios",
                                    header:
                                      "Ændring af tidspunkt vil rydde kurven og valg af borde",
                                    message:
                                      "Stedet kan have forskellige bordpakker, tilbud eller lukning af borde/områder på forskellige tidspunkter" +
                                      ", derfor vil din kurv blive ryddet og du skal vælge borde og drikkevare igen." +
                                      '\nHvis du er indforstået med det, og gerne vil forsætte, tryk "Ja, fortsæt"',
                                    buttons: [
                                      { text: "Fortryd" },
                                      {
                                        text: "Ja, fortsæt",
                                        handler: updateArrivalTime,
                                      },
                                    ],
                                  });

                                  return;
                                }

                                updateArrivalTime();
                              }}
                            />
                          )}
                        </div>
                      </InputSectionContent>
                    </InputSection>
                    <InputSection
                      id="tables"
                      disabled={
                        !floorPlan ||
                        !!practicalInfoErrors?.properties?.arrivalTime
                      }
                      invalid={
                        !!practicalInfoErrors?.properties?.tables ||
                        conflictingTables.length > 0
                      }
                      ignoreFirstInteraction={!!intent}
                    >
                      <InputSectionSummary>
                        <InputSectionSummaryTitle>
                          Plads
                        </InputSectionSummaryTitle>
                        <InputSectionSummaryContent>
                          {practicalInfo.tables &&
                            Array.from(practicalInfo.tables)
                              .map((t) => t.tableNumber)
                              .join(", ")}
                        </InputSectionSummaryContent>
                      </InputSectionSummary>
                      <InputSectionContent>
                        <div style={{ padding: "1rem" }}>{bookTableBox}</div>
                      </InputSectionContent>
                      <InputSectionError>
                        {conflictingTables.length > 0
                          ? "Du skal først fjerne de utilgængelige borde og vælge tilgængelige borde i stedet"
                          : printErrors(
                              practicalInfoErrors?.properties?.tables?.errors
                            )}
                      </InputSectionError>
                    </InputSection>
                    <InputSection
                      id="products"
                      disabled={
                        !bundles || !!practicalInfoErrors?.properties?.tables
                      }
                      invalid={
                        cartCount === 0 ||
                        !!practicalInfoErrors?.properties?.total
                      }
                      ignoreFirstInteraction={!!intent}
                    >
                      <InputSectionSummary
                        onClick={() => {
                          send({ type: "openBundlePickerModal" });
                        }}
                      >
                        <InputSectionSummaryTitle>
                          Drikkevarer
                        </InputSectionSummaryTitle>
                        <InputSectionSummaryContent>
                          {cartCount}
                        </InputSectionSummaryContent>
                      </InputSectionSummary>
                      <InputSectionError>
                        {printErrors(
                          practicalInfoErrors?.properties?.total?.errors
                        )}
                      </InputSectionError>
                    </InputSection>
                    <InputSection
                      id="comment"
                      disabled={cartCount === 0}
                      ignoreFirstInteraction={!!intent}
                      invalid={!!practicalInfoErrors?.properties?.comment}
                      disableCheckmark
                    >
                      <InputSectionSummary>
                        <InputSectionSummaryTitle>
                          {practicalInfo.comment !== "" &&
                          practicalInfo.comment !== undefined
                            ? "Rediger kommentar"
                            : "Tilknyt kommentar"}
                        </InputSectionSummaryTitle>
                        <InputSectionSummaryContent>
                          {practicalInfo.comment?.length ?? 0} / 500
                        </InputSectionSummaryContent>
                      </InputSectionSummary>
                      <InputSectionContent>
                        <TextField
                          style={{
                            margin: "1rem",
                            boxSizing: "border-box",
                            minWidth: "unset",
                          }}
                          placeholder="Din kommentar..."
                          value={practicalInfo.comment ?? ""}
                          onChange={(ev) => {
                            changePracticalInfo({
                              type: "setComment",
                              data: ev.target.value,
                            });
                          }}
                        />
                      </InputSectionContent>
                      <InputSectionError>
                        {printErrors(
                          practicalInfoErrors?.properties?.comment?.errors
                        )}
                      </InputSectionError>
                    </InputSection>
                    {cartCount > 0 && !snap.matches({ modal: "cart" }) && (
                      <GradientButton
                        className={css.OpenCartButton}
                        onClick={() => send({ type: "openCartModal" })}
                      >
                        Til kurven
                      </GradientButton>
                    )}
                  </div>
                )}
              </motion.div>

              {cartCount > 0 && !snap.matches({ modal: "cart" }) && (
                <CartBottomBar
                  id="bottom-bar"
                  onClick={() => send({ type: "openCartModal" })}
                />
              )}
            </IonContent>
          )}

        {snap.matches({ modal: "bundlePicker" }) && (
          <IonContent>
            <motion.div
              key="bundle-picker"
              {...dropInOutFromBottom}
              style={{ height: "calc(100% - var(--facebook-padding, 0px))" }}
            >
              <BundlePickerSelector
                onBundlePicked={(data) => {
                  send({ type: "closeModal" });
                  toast(SplitButtons, {
                    autoClose: 5000,
                    closeButton: false,
                    data: {
                      message: `Bordpakken${
                        data.title ? ` "${data.title}"` : ""
                      } blev tilføjet til bordet`,
                      buttons: [
                        {
                          text: "Tilføj en mere",
                          onClick() {
                            send({ type: "openBundlePickerModal" });
                          },
                        },
                        {
                          text: "Se kurv",
                          onClick() {
                            send({ type: "openCartModal" });
                          },
                        },
                      ],
                    },
                    // autoClose: 3_000,
                    position: "bottom-right",
                  });
                }}
                onDismiss={() => {
                  send({ type: "closeModal" });
                }}
                forDateAndTime={practicalInfo.arrivalTime}
                specialPricePeriods={lookupBy(
                  specialPricePeriods,
                  (pp) => pp.productId
                )}
                activePeriods={lookupBy(activePeriods, (ap) => ap.productId)}
                venueId={props.venueId}
                slots={{ BookTableBox: bookTableBox }}
                minPrice={minPrice}
              />
            </motion.div>
            <BottomBar id="bundle-picker-action-bar" theme="none" />
          </IonContent>
        )}

        {(snap.matches({ modal: "cart" }) ||
          snap.matches({ modal: "paymentMethodPicker" }) ||
          snap.matches({ ui: "paying" })) && (
          <IonContent>
            <motion.div
              key="cart"
              className={css.CartContent}
              {...dropInOutFromBottom}
            >
              <FloatingBackButton
                onClick={() => {
                  if (snap.matches({ modal: "paymentMethodPicker" })) {
                    send({ type: "openCartModal" });
                  } else {
                    send({ type: "closeModal" });
                  }
                }}
              />

              {/*existingBooking && existingBooking.status === "created" && (
                <h1 style={{ fontWeight: "bold" }}>
                  Halløj! Du fik ikke gennemført din betaling
                </h1>
              )*/}

              <motion.div layout="size" className={css.PracticalInfoCard}>
                <AnimatePresence mode="wait">
                  {snap.matches({ modal: "cart" }) && (
                    <motion.div
                      key="complex-info"
                      layout="position"
                      className={css.PracticalInfoComplex}
                      {...dropInOutFromTop}
                    >
                      <PracticalInfoSummary
                        onEdit={(target) => {
                          send({ type: "closeModal" });
                          if (target === "products") {
                            send({ type: "openBundlePickerModal" });
                            return;
                          }
                          inputGroup.open(target);
                        }}
                        practicalInfo={practicalInfo}
                      />
                      <div style={{ borderBottom: "solid 1px grey" }} />
                      <CartContent
                        bundles={indexBy(bundles, (b) => b.id)}
                        forDate={practicalInfo.arrivalTime}
                        specialPricePeriods={lookupBy(
                          snap.context.specialPricePeriods,
                          (p) => p.productId
                        )}
                      />
                    </motion.div>
                  )}
                  {snap.matches({ modal: "paymentMethodPicker" }) && (
                    <motion.div
                      key="simple-info"
                      layout="position"
                      className={css.PracticalInfoSimple}
                      {...dropInOutFromBottom}
                      transition={{ delay: 0.2 }}
                    >
                      <span>{practicalInfo.groupSize} pers.</span>
                      <span>{formatPrice(cartTotal)} kr.</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
              <AnimatePresence mode="wait">
                {snap.matches({ modal: "paymentMethodPicker" }) &&
                  !!stripePaymentToken && (
                    <motion.div
                      key="payment-method-picker"
                      style={{ width: "100%" }}
                    >
                      <div key={stripePaymentToken} style={{ margin: "auto" }}>
                        <Checkout
                          token={stripePaymentToken}
                          redirectUrl={function generateRedirectUrl() {
                            const url = `${window.location.protocol}//${
                              window.location.host
                            }${window.location.pathname}${queries({
                              bookingId: booking!.id,
                              venueId: venueId,
                              intent: "check-payment",
                              paymentToken: stripePaymentToken,
                            })}`;
                            return url;
                          }}
                          onPay={function onPay() {
                            Log.debug(`[Checkout][onPay] triggered`, {
                              states: snap.value,
                            });
                            send({ type: "next" });
                          }}
                          onSuccess={function onCheckoutSuccess() {
                            Log.debug(`[Checkout][onSuccess] succeeded!`, {
                              states: snap.value,
                            });
                            send({ type: "closeModal" });
                            send({ type: "next" });
                          }}
                          onError={function onCheckoutError(err: DeclineError) {
                            Log.debug(`[Checkout][onError] triggered`, {
                              states: snap.value,
                              error: err,
                            });
                            send({ type: "cancel" });
                            alert({
                              mode: "ios",
                              header: "Betaling fejlede",
                              message: translateStripeErrorToDanish(err),
                              buttons: [
                                { text: "Ok" },
                                {
                                  text: "Skriv til support",
                                  handler: openSupportChatHandler(
                                    booking?.id,
                                    err
                                  ),
                                },
                              ],
                            });
                          }}
                        />
                      </div>
                    </motion.div>
                  )}
              </AnimatePresence>
              {!snap.matches({ modal: "paymentMethodPicker" }) &&
                !snap.matches({ ui: "paying" }) && (
                  <div className={css.PlaceBookingButtonContainer}>
                    <GradientButton
                      disabled={!isInputValid || !settled}
                      className={css.PlaceBookingButton}
                      onClick={() => {
                        if (isInputValid) {
                          send({ type: "closeModal" });
                          send({ type: "openUpdateCustomerInfoModal" });
                        } else {
                          alert({
                            header:
                              "Du kan ikke booke indtil du har indtastet informationerne korrekt!",
                            mode: "ios",
                          });
                        }
                      }}
                    >
                      {booking?.status === "pending" && <IonSpinner />}
                      {booking?.status === "pending"
                        ? "Opret ny booking!"
                        : "Book bordet!"}
                    </GradientButton>
                  </div>
                )}
            </motion.div>
          </IonContent>
        )}

        {snap.matches({ modal: "login" }) && (
          <IonContent>
            <motion.div
              key="modal"
              style={{ width: "100%" }}
              {...dropInOutFromBottom}
            >
              <LoginPage
                onRedirect={doNothing}
                onBack={function onBack() {
                  send({ type: "closeModal" });
                }}
                onDone={function onDone() {
                  send({ type: "next" });
                }}
              />
            </motion.div>
          </IonContent>
        )}

        {snap.matches({ modal: "updateCustomerInfo" }) && (
          <IonContent>
            <motion.div
              key="updateCustomerInfo"
              style={{ width: "100%", height: "100%" }}
              {...dropInOutFromBottom}
            >
              <UpdateUserPageApiHandler
                onBack={function onBack() {
                  send({ type: "closeModal" });
                }}
                onDone={function onDone() {
                  send({ type: "createBooking" });
                  send({ type: "openCartModal" });
                }}
              />
              <FloatingBackButton
                style={{ zIndex: 10000 }}
                onClick={() => {
                  send({ type: "openCartModal" });
                }}
              />
            </motion.div>
          </IonContent>
        )}
      </AnimatePresence>
      <Spinner
        data-testid="spinner"
        reason={
          snap.matches({ background: { booking: "creating" } })
            ? "Opretter booking"
            : snap.matches({ background: { payment: "creating" } })
            ? "Forbereder betaling"
            : snap.matches({ ui: "paying" }) && "Betaler"
        }
      />

      <ToastContainer toastClassName={css.Toast} />
      {!snap.matches({ ui: "initializing" }) &&
        !snap.matches({ modal: "bundlePicker" }) &&
        !snap.matches({ modal: "updateCustomerInfo" }) && (
          <ChatSupportBubble
            offsetBottom={
              !snap.matches({ modal: "cart" }) &&
              !snap.matches({ modal: "paymentMethodPicker" }) &&
              (cartCount > 0 ||
                (booking?.order?.orderLines?.length ?? 0) > 0 ||
                snap.matches({ modal: "bundlePicker" }))
                ? 70
                : 0
            }
            attributes={{
              venueId: props.venueId,
              venueName: snap.context.venue?.name,
              practicalInfo: snap.context.practicalInfoChanges.value,
              cart: [...snap.context.cart.entries()],
            }}
          />
        )}
      <Spinner
        reason={
          snap.matches({ ui: "initializing" })
            ? "Gør klar"
            : snap.matches({ background: { venue: "fetching" } })
            ? "Henter venue"
            : snap.matches({
                background: { specialOpeningHours: "fetching" },
              }) && "Henter åbningstider"
        }
      />
    </IonPage>
  );
}

function useBookingCache(props: { venueId: string }) {
  return usePersistedCache<Snapshot<unknown>>({
    id: `greeter.${props.venueId}.booking-beta.snapshot`,
    schema: BookingContextPersistedSnapshotSchema,
    policy: {
      staleAfter: minutesToMilliseconds(15),
    },
  });
}

function useCartCache(props: { venueId: string }) {
  return usePersistedCache<Snapshot<unknown>>({
    id: `greeter.${props.venueId}.booking.cart.snapshot`,
    schema: PersistedSnapshotSchema(CartContextSchema),
    policy: {
      staleAfter: minutesToMilliseconds(15),
    },
  });
}

export type CreateBookingPageV2WithProvidersProps = {
  venueId?: string;
  api: BookingMachineApiRequirements;
};
export function CreateBookingPageV2WithProviders(
  props: CreateBookingPageV2WithProvidersProps
) {
  const searchParams = useSearchParams();
  const intent: Intent | null = searchParams.get("intent") as Intent;
  const tab = useDetectTab();
  const routeMatch = useRouteMatch<{ venueId: string }>(
    BookRoute.get(tab).template()
  );
  const params = routeMatch?.params;
  const cartCache = useCartCache({
    venueId: props.venueId ?? params?.venueId ?? "NONE",
  });

  return (
    <CartActorProvider
      options={{
        snapshot: intent === "check-payment" ? cartCache.get() : undefined,
      }}
    >
      <InputGroupProvider>
        <CreateBookingPageV2
          api={props.api}
          venueId={props.venueId ?? params?.venueId ?? ""}
        />
      </InputGroupProvider>
    </CartActorProvider>
  );
}
